import { KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';
import { UseInteractiveTileProps } from '.';
import useOnFocusOut from '../../../hooks/useOnFocusOut';
import { getAllFocusable } from '../../../util/focus';
import { useOnOutsideMouseDown } from '../../PropertyPaneLayout/useOnOutsideMouseDown';

export const useInteractiveTile = (props: UseInteractiveTileProps) => {
  const hasFocusRef = useRef(false);
  const [isInteractive, setIsInteractive] = useState(false);
  // We need to turn focus lock off when user clicks outside.
  useOnOutsideMouseDown(
    props.tileRef,
    useCallback(() => {
      setIsInteractive(false);
    }, [])
  );
  const { onFocus, onBlur } = useOnFocusOut(() => {
    hasFocusRef.current = false;
  });

  // Set focus on the TileContainer after switching from interactive mode to browse mode
  useEffect(() => {
    if (hasFocusRef.current && !isInteractive && props.tileRef.current !== document.activeElement) {
      props.tileRef.current?.focus({ preventScroll: true });
    }
  }, [isInteractive, props.tileRef]);

  // Toggle the focusability of the body/footer elements based on isInteractive value
  // The body/footer section may contain elements that focuslock doesn't handle correctly. E.g. input tag with
  // readonly attribute. We need to manually toggle tabindex for these.
  useEffect(() => {
    const focusableElements: HTMLElement[] = [];
    for (const customContentRef of props.customContentRefs ?? []) {
      if (!customContentRef.current) continue;
      focusableElements.push(...getAllFocusable(customContentRef.current));
    }

    for (const element of focusableElements) {
      element.setAttribute('tabindex', isInteractive ? '0' : '-1');
    }
    return () => {
      for (const element of focusableElements) {
        element.setAttribute('tabindex', '0');
      }
    };
  }, [props.customContentRefs, isInteractive]);
  return {
    isInteractive,
    onClick: () => {
      // Implicitly change to interactive mode.
      setIsInteractive(true);
    },
    onFocus: () => {
      onFocus();
      hasFocusRef.current = true;
    },
    onBlur,
    onKeyDown: (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.defaultPrevented) return;

      if (e.key === 'F2') {
        setIsInteractive((prev) => !prev);
        e.preventDefault();
      } else if (isInteractive) {
        if (
          [
            'ArrowDown',
            'ArrowRight',
            'ArrowLeft',
            'ArrowUp',
            'Home',
            'End',
            'PageUp',
            'PageDown',
          ].includes(e.key)
        ) {
          // Supress browser scrolling
          e.preventDefault();
        } else if (e.key === 'Escape') {
          // Escape cancels interactive mode.
          setIsInteractive(false);
          e.preventDefault();
        }
        // When in interactive mode, no keyboard event should be handled outside of the tile
        e.stopPropagation();
      }
    },
  };
};
